import React, { useRef, useState } from 'react';
import emailjs from 'emailjs-com';
import { toast } from 'react-toastify';
import { motion, useReducedMotion } from 'framer-motion';
import { Reveal } from './MotionPrimitives';

const orbs = [
  {
    className: 'left-[-8%] top-[12%] h-72 w-72 bg-accent/25',
    animate: { x: [0, 40, -10, 0], y: [0, -30, 20, 0], scale: [1, 1.12, 0.96, 1] },
    duration: 18,
  },
  {
    className: 'right-[-6%] top-[28%] h-80 w-80 bg-violet-500/20',
    animate: { x: [0, -35, 15, 0], y: [0, 40, -20, 0], scale: [1, 0.92, 1.1, 1] },
    duration: 22,
  },
  {
    className: 'bottom-[8%] left-[18%] h-64 w-64 bg-cyan-400/15',
    animate: { x: [0, 25, -30, 0], y: [0, -25, 15, 0], scale: [1, 1.08, 0.94, 1] },
    duration: 20,
  },
];

const ringPulse = {
  initial: { scale: 0.7, opacity: 0.35 },
  animate: {
    scale: [0.75, 1.35],
    opacity: [0.28, 0],
  },
};

export default function ContactLead() {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', description: '' });
  const reduce = useReducedMotion();

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    emailjs
      .sendForm('service_3d5bhy6', 'template_ve0h2nq', formRef.current, '0hws8bYSoW3KWfjU-')
      .then(() => {
        toast.success('Thank you! Our team will call you soon.');
        setForm({ name: '', mobile: '', email: '', description: '' });
      })
      .catch(() => toast.error('Something went wrong. Please try again.'))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <section id="contact" className="section-pad relative overflow-hidden bg-black">
      {/* Atmosphere layer */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {/* soft mesh wash */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.1),transparent_50%)]" />

        {/* faint grid that drifts */}
        {!reduce && (
          <motion.div
            className="absolute inset-[-20%] opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px)',
              backgroundSize: '56px 56px',
              maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 72%)',
              WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 72%)',
            }}
            animate={{ x: [0, 28, 0], y: [0, -18, 0] }}
            transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
          />
        )}

        {/* floating glow orbs */}
        {orbs.map((orb, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full blur-[90px] ${orb.className}`}
            animate={reduce ? undefined : orb.animate}
            transition={
              reduce
                ? undefined
                : { duration: orb.duration, repeat: Infinity, ease: 'easeInOut' }
            }
          />
        ))}

        {/* expanding rings behind the form */}
        {!reduce && (
          <div className="absolute left-1/2 top-[58%] h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 sm:h-[520px] sm:w-[520px]">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="absolute inset-0 rounded-full border border-accent/25"
                variants={ringPulse}
                initial="initial"
                animate="animate"
                transition={{
                  duration: 5.5,
                  repeat: Infinity,
                  ease: 'easeOut',
                  delay: i * 1.8,
                }}
              />
            ))}
            <div className="absolute inset-[28%] rounded-full border border-white/5 bg-accent/5 blur-sm" />
          </div>
        )}

        {/* soft horizontal light beam */}
        {!reduce && (
          <motion.div
            className="absolute left-1/2 top-[42%] h-px w-[min(90%,720px)] -translate-x-1/2 bg-gradient-to-r from-transparent via-accent/50 to-transparent"
            animate={{ opacity: [0.15, 0.55, 0.15], scaleX: [0.85, 1.05, 0.85] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}

        {/* tiny floating dots */}
        {!reduce &&
          [
            { top: '18%', left: '12%', delay: 0 },
            { top: '26%', right: '16%', delay: 1.2 },
            { top: '72%', left: '22%', delay: 0.6 },
            { top: '68%', right: '18%', delay: 2 },
            { top: '40%', left: '8%', delay: 1.6 },
          ].map((dot, i) => (
            <motion.span
              key={i}
              className="absolute h-1.5 w-1.5 rounded-full bg-accent/70 shadow-[0_0_12px_rgba(56,189,248,0.8)]"
              style={{ top: dot.top, left: dot.left, right: dot.right }}
              animate={{ y: [0, -14, 0], opacity: [0.25, 0.9, 0.25] }}
              transition={{
                duration: 4.5 + i * 0.4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: dot.delay,
              }}
            />
          ))}
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="pill-light">Contact</span>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
            Talk to us about <span className="grad-text">your shop</span>
          </h2>
          <p className="mt-4 text-mute">
            One store or a full distribution setup. Tell us how you sell today and we will help you
            map Zuntly to it.
          </p>
          <p className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-mute">
            <a href="tel:+919676568515" className="font-semibold text-white transition hover:text-accent">
              +91 96765 68515
            </a>
            <span className="hidden text-white/20 sm:inline">|</span>
            <a href="mailto:ecom@zuntly.com" className="transition hover:text-white">
              ecom@zuntly.com
            </a>
          </p>
        </Reveal>

        <Reveal delay={0.1} className="relative mx-auto mt-12 max-w-xl">
          <div className="pointer-events-none absolute -inset-4 rounded-[1.75rem] bg-accent/10 blur-2xl" />
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="relative rounded-2xl border border-white/10 bg-card/90 p-6 shadow-glow backdrop-blur-xl sm:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                required
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                className="rounded-xl border border-white/10 bg-black/80 px-4 py-3 text-sm text-white outline-none transition focus:border-accent"
              />
              <input
                required
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                placeholder="Mobile"
                className="rounded-xl border border-white/10 bg-black/80 px-4 py-3 text-sm text-white outline-none transition focus:border-accent"
              />
            </div>
            <input
              required
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
              className="mt-4 w-full rounded-xl border border-white/10 bg-black/80 px-4 py-3 text-sm text-white outline-none transition focus:border-accent"
            />
            <textarea
              required
              name="description"
              rows={4}
              value={form.description}
              onChange={handleChange}
              placeholder="How many shops? What do you sell?"
              className="mt-4 w-full resize-none rounded-xl border border-white/10 bg-black/80 px-4 py-3 text-sm text-white outline-none transition focus:border-accent"
            />
            <button type="submit" disabled={isSubmitting} className="btn-grad mt-6 w-full">
              {isSubmitting ? 'Sending…' : 'Get Started'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
